import { createContext, useContext, useState } from 'react';

import styled from 'styled-components';

import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { useOverridableDebouncedState } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { hideModal } from '@components/modal';
import { NavSegmentedButtons } from '@components/nav-segmented-buttons/nav-segmented-buttons';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { noop, urlToFile } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { createNumberArray } from '@utils/number-arrays';

import { UnsplashImageResult, UnsplashResponse } from './types';
import { useUnsplashApi, useUnsplashSearchQuery } from './unsplash-api';

type UnsplashContextData = {
  photo?: UnsplashImageResult;
  setPhoto: (photo: UnsplashImageResult) => void;
};

const UnsplashContext = createContext<UnsplashContextData>({
  photo: undefined,
  setPhoto: noop,
});

export type UnsplashProps = {
  onSelect: (photoFile: File) => void;
};

const NUMBER_OF_ROWS = 3;
const ITEMS_IN_ROW = 3;
const PAGE_SIZE = NUMBER_OF_ROWS * ITEMS_IN_ROW;
const DEBOUNCE_DELAY = 500;
const IMAGE_HEIGHT = 120;

export const Unsplash = ({ onSelect }: UnsplashProps) => {
  const { t } = useScopedTranslate(locale);
  const [photo, setPhoto] = useState<UnsplashContextData['photo']>();
  const [{ page, search }, setSearch, setSearchInstantly] = useOverridableDebouncedState(
    { page: 1, search: '' },
    DEBOUNCE_DELAY,
  );
  const { triggerUnsplashDownload } = useUnsplashApi();

  const handleSearch = (input: string) => {
    setSearch({ page: 1, search: input });
  };

  const handlePageChange = (offset: number) => {
    setSearchInstantly({ page: page + offset, search });
  };

  const unsplashQuery = useUnsplashSearchQuery({
    search,
    page,
    pageSize: PAGE_SIZE,
  });
  const results = unsplashQuery.data?.results ?? [];
  const totalPages = unsplashQuery.data?.totalPages ?? 0;

  const handleSelect = async () => {
    const ixid = photo!.links.downloadLocation.split('=').pop()!;
    triggerUnsplashDownload({ slug: photo!.slug, ixid });

    const file = await urlToFile({ url: photo!.urls.regular, filename: `${photo?.slug}.jpg` });
    onSelect?.(file);
    hideModal();
  };

  return (
    <UnsplashContext.Provider value={{ photo, setPhoto }}>
      <Stack space={6}>
        <SearchBox placeholder={t('searchbarPlaceholder')} onChange={handleSearch} />
        <div style={{ height: pxToRem(408) }}>
          {results.length || unsplashQuery.isLoading ? (
            <Photos results={results} />
          ) : (
            <EmptySearchState skipMargin />
          )}
        </div>
      </Stack>
      <TabActions>
        <Stack align="flex-end">
          <Stack orientation="horizontal" space={4}>
            {(results.length > 0 || unsplashQuery.isFetching) && (
              <NavSegmentedButtons
                isDisabled={unsplashQuery.isFetching}
                onBack={() => handlePageChange(-1)}
                isBackDisabled={page === 1}
                onForward={() => handlePageChange(1)}
                isForwardDisabled={page === totalPages}
              />
            )}
            <CancelSaveButtons
              alwaysShow
              onCancel={hideModal}
              isSaveDisabled={!photo}
              onSave={handleSelect}
            />
          </Stack>
        </Stack>
      </TabActions>
    </UnsplashContext.Provider>
  );
};

// Not Modal.Footer: this bar is per-tab and lives inside Modal.Content, so the
// footer's border-top would draw across the middle of the results.
const TabActions = styled.div({
  marginTop: pxToRem(24),
});

type PhotoProps = Pick<UnsplashResponse, 'results'>;

const Photos = ({ results }: PhotoProps) => {
  return (
    <Stack space={4}>
      {createNumberArray(0, NUMBER_OF_ROWS).map((rowNumber) => {
        const rowResults = results.slice(rowNumber * ITEMS_IN_ROW, (rowNumber + 1) * ITEMS_IN_ROW);
        return <PhotoRow key={rowNumber} results={rowResults} />;
      })}
    </Stack>
  );
};

const PhotoRow = ({ results }: PhotoProps) => {
  const { photo, setPhoto } = useContext(UnsplashContext);

  return (
    <Stack orientation="horizontal" space={4}>
      {results.map((result) => (
        <ThumbnailContainer
          key={result.id}
          onClick={() => setPhoto(result)}
          isSelected={result.id === photo?.id}
        >
          <Thumbnail src={result.urls.small} alt={result.altDescription} />
          <Attribution photo={result} />
        </ThumbnailContainer>
      ))}
    </Stack>
  );
};

type AttributionProps = {
  photo: UnsplashImageResult;
};

const Attribution = ({ photo }: AttributionProps) => {
  const link = `${photo.user.links.html}?utm_source=50skills&utm_medium=referral`;
  return (
    <a href={link} target="_blank" className={ATTRIBUTION_CLASSNAME}>
      <Text text={photo.user.name} color="white100" font="regular12" />
    </a>
  );
};

const ATTRIBUTION_CLASSNAME = 'attribution';
const ThumbnailContainer = styled.div<{ isSelected: boolean }>(({ isSelected }) => {
  return {
    position: 'relative',
    flex: 1,
    height: pxToRem(IMAGE_HEIGHT),
    overflow: 'hidden',
    cursor: 'pointer',
    borderRadius: pxToRem(6),
    borderColor: 'transparent',
    borderWidth: pxToRem(2),
    borderStyle: 'solid',
    [`&:hover .${ATTRIBUTION_CLASSNAME}`]: {
      opacity: 1,
    },
    [`.${ATTRIBUTION_CLASSNAME}`]: {
      position: 'absolute',
      bottom: 0,
      width: '100%',
      height: pxToRem(32),
      backgroundColor: theme.color.uiTrans50,
      opacity: 0,
      transition: 'opacity 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      padding: pxToRem(8),
    },

    ...(isSelected && {
      borderColor: theme.color.blue100,
    }),
  };
});

const Thumbnail = styled.img({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
});

const locale = {
  en: {
    noResults: 'No results',
    searchbarPlaceholder: 'Search free images from Unsplash...',
    searchPlaceholderHeader: 'Type to search',
    searchPlaceholderContent: 'Please insert what you are looking for',
  },
};
